import { PropsWithChildren, ReactNode, useMemo } from 'react';
import { css } from '@emotion/react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { DemoChip, TruncatableHeaderTitle } from '@frontend/analytics-shared';
import { Chart, GaugeChartAppearance } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ContentLoader, Heading, NakedButton, Text, Truncated, useAlert, useTooltip } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { usePaNavigate } from './hooks/use-pa-navigate';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { PracticeAnalyticsInfoTips } from './practice-analytics-info-tips';

const activePatientsQuery = gql`
  query {
    location {
      activePatients {
        historicalData(count: 24, step: WEEKLY) {
          total
          label
          timestamp
        }
        total
      }
    }
  }
`;

const activePatientsScheduledQuery = gql`
  query {
    location {
      activePatientsScheduled {
        percentage
        benchmarks {
          value
          label
        }
      }
    }
  }
`;

const chartHeight = 50;

const gaugeAppearance = (verySmallWidth?: boolean): GaugeChartAppearance => {
  return {
    backFillColor: theme.colors.neutral5,
    backFillStrokeWidth: 0,
    borderRadius: 15,
    endAngle: -10,
    height: chartHeight,
    hideLabel: true,
    hideTooltip: true,
    innerRadius: verySmallWidth ? 25 : 30,
    margin: { top: 40 },
    outerRadius: verySmallWidth ? 35 : 42,
    startAngle: 190,
    width: verySmallWidth ? 70 : 85,
  };
};

type Props = {
  contentMaxWidth?: string | number;
  infoTip: ReactNode;
  isLoading?: boolean;
  label: string;
  onClick?: () => void;
  title: string;
  tooltipValue?: string | number;
  trackingId?: string;
  value?: string | number;
  verySmallWidth?: boolean;
};

const Card = ({
  children,
  contentMaxWidth,
  infoTip,
  isLoading,
  label,
  onClick,
  title,
  tooltipValue,
  trackingId,
  value,
  verySmallWidth,
}: PropsWithChildren & Props) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({ placement: 'top-start' });

  return (
    <>
      <NakedButton css={styles.card} onClick={onClick} trackingId={trackingId}>
        <TruncatableHeaderTitle title={title} infoTip={infoTip} style={{ marginBottom: theme.spacing(1) }} />
        <div css={styles.cardContent} style={{ gap: verySmallWidth ? theme.spacing(1) : theme.spacing(2) }}>
          <div style={{ maxWidth: contentMaxWidth ?? (verySmallWidth ? theme.spacing(7) : '100%') }}>
            <Text {...triggerProps} style={{ fontSize: theme.fontSize(24) }} weight='bold'>
              {value}
            </Text>
            <Truncated as='span' css={styles.truncatedCardLabel}>
              {label}
            </Truncated>
          </div>
          <div className='chart'>{children}</div>
        </div>
        <ContentLoader show={isLoading} size='small' />
      </NakedButton>
      {tooltipValue && <Tooltip {...tooltipProps}>{tooltipValue}</Tooltip>}
    </>
  );
};

type SmallModeCardsProps = {
  verySmallWidth?: boolean;
};

export const SummaryMetricSmallMode = ({ verySmallWidth }: SmallModeCardsProps) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { demoData, filters, isDemoAccount } = usePracticeAnalyticsShallowStore('demoData', 'filters', 'isDemoAccount');
  const navigate = usePaNavigate();

  const { data: activePatientsData, isLoading: isLoadingActivePatientsData } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `summaryMetricActivePatients-${isDemoAccount}-${JSON.stringify(filters.locations)}`
    ),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.ActivePatientsResponse>({
            locationIds: filters.locations,
            queries: [activePatientsQuery],
          }),
    onError: () => {
      alert.error(t("Couldn't load the active patients data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.activePatients : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const aggregatedActivePatientsData = useMemo(() => {
    const aggregatedData = PracticeAnalyticsAggregations.activePatientsSummary(activePatientsData);
    const { activePatients } = aggregatedData;
    const historicalDataLength = activePatients?.historicalData?.length || 0;
    const thisPeriodPatients = activePatients?.historicalData?.slice(historicalDataLength / 2) || [];

    return {
      trendLine: thisPeriodPatients.map(({ total }) => total),
      totalPatients: activePatients?.total || 0,
    };
  }, [activePatientsData]);

  const { data: activePatientsScheduledData, isLoading: isLoadingActivePatientsScheduledData } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `summaryMetricActivePatientsScheduled-${isDemoAccount}-${JSON.stringify(filters.locations)}`
    ),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.ActivePatientsScheduledResponse>({
            locationIds: filters.locations,
            queries: [activePatientsScheduledQuery],
          }),
    onError: () => {
      alert.error(t("Couldn't load the active scheduled patients data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.activePatientsScheduled : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const aggregatedActivePatientsScheduledData = useMemo(() => {
    return PracticeAnalyticsAggregations.activePatientsScheduledSummary(
      activePatientsScheduledData,
      filters.locations?.length
    );
  }, [activePatientsScheduledData, filters.locations?.length]);

  return (
    <section css={styles.mainWrapper}>
      <div className='title-wrapper'>
        {isDemoAccount && <DemoChip />}
        <Heading className='heading' level={3}>
          <Truncated as='span'>{t('Patient Overview')}</Truncated>
        </Heading>
      </div>
      <div css={styles.cardsWrapper}>
        <Card
          title={t('Active Patients')}
          infoTip={<PracticeAnalyticsInfoTips tip='activePatients' />}
          contentMaxWidth={verySmallWidth ? theme.spacing(9) : '100%'}
          isLoading={isLoadingActivePatientsData}
          label={t('Patients')}
          onClick={() => {
            navigate({
              to: '/analytics/practice/demographics',
            });
          }}
          trackingId='pa-small-mode-active-patients'
          value={
            verySmallWidth
              ? formatters.shortenNumber(aggregatedActivePatientsData?.totalPatients, true)
              : formatters.value(aggregatedActivePatientsData?.totalPatients)
          }
          tooltipValue={
            (aggregatedActivePatientsData?.totalPatients || 0) > 999
              ? formatters.value(aggregatedActivePatientsData?.totalPatients)
              : undefined
          }
          verySmallWidth={verySmallWidth}
        >
          <Chart.TinyAreaChart
            color={theme.colors.critical30}
            data={aggregatedActivePatientsData.trendLine}
            height={chartHeight}
          />
        </Card>

        <Card
          title={t('Active Scheduled')}
          infoTip={<PracticeAnalyticsInfoTips tip='activePatientsScheduled' />}
          isLoading={isLoadingActivePatientsScheduledData}
          label={t('Your Practice')}
          trackingId='pa-small-mode-active-patients-scheduled'
          onClick={() => {
            navigate({
              to: '/analytics/practice/unscheduled-patients',
            });
          }}
          value={formatters.appendPercent(aggregatedActivePatientsScheduledData?.activePracticePercent)}
          verySmallWidth={verySmallWidth}
        >
          <Chart
            colors={{ value: theme.colors.warning20 }}
            emptyStateConfig={{
              hideEmptyState: true,
            }}
            noBorder
            style={{ marginBottom: 0 }}
          >
            <Chart.GaugeChart
              appearance={gaugeAppearance(verySmallWidth)}
              value={aggregatedActivePatientsScheduledData?.activePracticePercent}
            />
          </Chart>
        </Card>
      </div>
    </section>
  );
};

const styles = {
  mainWrapper: css`
    margin-bottom: ${theme.spacing(2)};

    .title-wrapper {
      align-items: center;
      display: flex;
      gap: ${theme.spacing(1)};
      margin-bottom: ${theme.spacing(2)};
      min-width: 0;
      overflow: hidden;

      .heading {
        align-items: center;
        display: flex;
        flex-shrink: 1;
        gap: ${theme.spacing(0.5)};
        min-width: 0;

        button {
          margin-top: ${theme.spacing(0.25)};
        }
      }
    }
  `,

  cardsWrapper: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
  `,

  card: css`
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.neutral20};
    flex: 1;
    max-width: 100%;
    overflow: hidden;
    overflow: hidden;
    padding: ${theme.spacing(2)};
    position: relative;
    text-align: left;

    &:hover {
      background-color: ${theme.colors.neutral5};
    }

    @container (max-width: 350px) {
      min-width: 200px;
    }

    .title-wrapper {
      margin-bottom: ${theme.spacing(1)};
    }
  `,

  cardContent: css`
    display: flex;

    .chart {
      align-items: center;
      display: flex;
      flex: 1;
      justify-content: flex-end;
    }
  `,

  truncatedCardLabel: css`
    color: ${theme.font.colors.subdued};
    display: block;
    font-size: ${theme.font.size.small};
    max-width: 100%;
  `,
};
